import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import OverdueConsultationWidget from "./OverdueConsultationWidget";
import EmailTrackingCard from "./EmailTrackingCard";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format, startOfDay, endOfDay, subDays, subMonths, startOfMonth, isSameDay } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import {
  DollarSign, Users, UserCheck, Stethoscope, Building2, ShoppingCart,
  TrendingUp, Clock, CheckCircle, XCircle, RotateCcw, FileText,
  Pill, CalendarCheck, Briefcase, AlertTriangle, Activity, CreditCard,
  UserPlus, ClipboardList, CalendarIcon, Mail, MessageSquare, Truck,
  Package, UserCog, MousePointerClick, ShieldCheck, Download, Settings,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area, Legend,
} from "recharts";

const COLORS = [
  "hsl(var(--primary))", "hsl(var(--champagne))", "hsl(var(--muted-foreground))",
  "hsl(var(--destructive))", "#94a3b8", "#8b5cf6", "#f59e0b", "#10b981",
];

const fmt = (n: number) => n.toLocaleString("en-US");
const fmtUsd = (cents: number) => `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;

interface SparkPoint { v: number; label: string }

interface KPICardProps {
  label: string;
  value: string | number;
  icon: React.ElementType;
  color?: string;
  sub?: string;
  onClick?: () => void;
  sparkData?: SparkPoint[];
  sparkColor?: string;
  change?: number | null;
  prevValue?: string | number | null;
}

const SparkTooltipContent = ({ active, payload }: any) => {
  if (!active || !payload?.[0]) return null;
  const { v, label } = payload[0].payload as SparkPoint;
  return (
    <div className="rounded-md border border-border bg-card px-2 py-1 shadow-sm text-[10px] leading-tight">
      <p className="font-medium text-foreground">{typeof v === "number" && v % 1 !== 0 ? `$${v.toFixed(2)}` : v}</p>
      <p className="text-muted-foreground">{label}</p>
    </div>
  );
};

const ChangeIndicator = ({ change, prevValue }: { change: number; prevValue?: string | number | null }) => {
  if (!isFinite(change)) return null;
  const isPositive = change > 0;
  const isZero = change === 0;
  const isSignificant = Math.abs(change) > 20;

  const indicator = (
    <span className={cn(
      "inline-flex items-center gap-0.5 font-medium cursor-default rounded-sm px-1 py-0.5 transition-colors",
      isSignificant ? "text-[10px]" : "text-[10px]",
      isZero
        ? "text-muted-foreground"
        : isPositive
          ? isSignificant
            ? "text-primary bg-primary/10 animate-pulse"
            : "text-primary"
          : isSignificant
            ? "text-destructive bg-destructive/10 animate-pulse"
            : "text-destructive"
    )}>
      {!isZero && (
        <span className={cn("inline-block", !isPositive && "rotate-180")}>↑</span>
      )}
      {isZero ? "—" : `${Math.abs(change).toFixed(1)}%`}
    </span>
  );

  if (prevValue == null) return indicator;

  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Popover>
        <PopoverTrigger asChild>
          {indicator}
        </PopoverTrigger>
        <PopoverContent side="top" align="start" className="w-auto px-2.5 py-1.5 text-[10px] leading-tight" sideOffset={4}>
          <p className="text-muted-foreground">Previous period</p>
          <p className="font-medium text-foreground">{prevValue}</p>
        </PopoverContent>
      </Popover>
    </span>
  );
};



const KPICard = ({ label, value, icon: Icon, color = "text-primary", sub, onClick, sparkData, sparkColor, change, prevValue }: KPICardProps) => {
  const significant = change != null && isFinite(change) && Math.abs(change) > 20;
  const sigPositive = significant && change! > 0;
  return (
  <Card
    className={cn(
      "border-border transition-colors",
      onClick && "cursor-pointer hover:border-primary/40",
      significant && (sigPositive ? "border-primary/50 shadow-[0_0_8px_-2px_hsl(var(--primary)/0.25)]" : "border-destructive/50 shadow-[0_0_8px_-2px_hsl(var(--destructive)/0.25)]")
    )}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon size={15} className={color} />
      </div>
      <div className="flex items-end justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-baseline gap-1.5">
            <p className="font-display text-2xl text-foreground">{value}</p>
            {change != null && <ChangeIndicator change={change} prevValue={prevValue} />}
          </div>
          {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        {sparkData && sparkData.length > 1 && (
          <div className="w-16 h-8 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id={`spark-${label.replace(/\s/g, "")}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={sparkColor || "hsl(var(--primary))"} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={sparkColor || "hsl(var(--primary))"} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <Tooltip content={<SparkTooltipContent />} cursor={false} />
                <Area type="monotone" dataKey="v" stroke={sparkColor || "hsl(var(--primary))"} strokeWidth={1.5} fill={`url(#spark-${label.replace(/\s/g, "")})`} dot={false} activeDot={{ r: 2, strokeWidth: 0 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
  );
};

const SectionTitle = ({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) => (
  <h3 className="font-display text-lg flex items-center gap-2 text-foreground">
    <Icon size={16} className="text-primary" /> {children}
  </h3>
);

const REFRESH_INTERVAL_MS = 30_000;

type DatePreset = "7d" | "30d" | "90d" | "ytd" | "all" | "custom";

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
  { value: "90d", label: "90 days" },
  { value: "ytd", label: "YTD" },
  { value: "all", label: "All time" },
  { value: "custom", label: "Custom" },
];

const getPresetRange = (preset: DatePreset): { from: Date | null; to: Date } => {
  const now = new Date();
  switch (preset) {
    case "7d": return { from: subDays(now, 7), to: now };
    case "30d": return { from: subDays(now, 30), to: now };
    case "90d": return { from: subDays(now, 90), to: now };
    case "ytd": return { from: new Date(now.getFullYear(), 0, 1), to: now };
    case "all": return { from: null, to: now };
    default: return { from: null, to: now };
  }
};

interface AdminKPIOverviewProps {
  onNavigateToTab?: (tab: string) => void;
}

const AdminKPIOverview = ({ onNavigateToTab }: AdminKPIOverviewProps) => {
  const nav = (tab: string) => onNavigateToTab?.(tab);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [refreshing, setRefreshing] = useState(false);
  const [preset, setPreset] = useState<DatePreset>("all");
  const [customFrom, setCustomFrom] = useState<Date | undefined>(undefined);
  const [customTo, setCustomTo] = useState<Date | undefined>(undefined);
  const alertSentRef = useRef<string>("");
  const { toast } = useToast();
  const [alertThreshold, setAlertThreshold] = useState<number>(() => {
    const saved = localStorage.getItem("kpi_alert_threshold");
    return saved ? Number(saved) : 50;
  });
  const handleThresholdChange = (val: string) => {
    const n = Math.max(1, Math.min(500, Number(val) || 50));
    setAlertThreshold(n);
    localStorage.setItem("kpi_alert_threshold", String(n));
  };

  const dateRange = useMemo(() => {
    if (preset === "custom") {
      return { from: customFrom || null, to: customTo || new Date() };
    }
    return getPresetRange(preset);
  }, [preset, customFrom, customTo]);

  const inRange = useCallback((dateStr: string) => {
    if (!dateRange.from) return true;
    const d = new Date(dateStr);
    return d >= startOfDay(dateRange.from) && d <= endOfDay(dateRange.to);
  }, [dateRange]);

  // Previous period range (same duration, shifted back)
  const prevRange = useMemo(() => {
    if (!dateRange.from) return null; // "all" has no previous period
    const spanMs = dateRange.to.getTime() - dateRange.from.getTime();
    const prevTo = new Date(dateRange.from.getTime());
    const prevFrom = new Date(dateRange.from.getTime() - spanMs);
    return { from: prevFrom, to: prevTo };
  }, [dateRange]);

  const inPrevRange = useCallback((dateStr: string) => {
    if (!prevRange) return false;
    const d = new Date(dateStr);
    return d >= startOfDay(prevRange.from) && d < startOfDay(prevRange.to);
  }, [prevRange]);

  const pctChange = (current: number, previous: number): number | null => {
    if (!prevRange) return null; // no comparison for "all time"
    if (previous === 0 && current === 0) return 0;
    if (previous === 0) return 100;
    return ((current - previous) / previous) * 100;
  };

  const load = useCallback(async (isBackground = false) => {
    if (!isBackground) setLoading(true);
    else setRefreshing(true);

    const [
      { data: orders },
      { data: profiles },
      { data: roles },
      { data: credentials },
      { data: pharmacies },
      { data: assessments },
      { data: prescriptions },
      { data: appointments },
      { data: salesSettings },
      { data: payouts },
      { data: messages },
      { data: providerPayouts },
      { data: pharmacyStaff },
      { data: progressLogs },
      { data: notifPrefs },
      { data: referralClicks },
    ] = await Promise.all([
      supabase.from("orders").select("id, order_type, status, amount_cents, created_at, salesperson_id, commission_rate").order("created_at", { ascending: false }).limit(1000),
      supabase.from("profiles").select("user_id, created_at, assigned_provider_id, state").order("created_at", { ascending: false }).limit(1000),
      supabase.from("user_roles").select("user_id, role"),
      supabase.from("provider_credentials").select("id, credential_status, specialty, license_expiry, user_id"),
      supabase.from("partner_pharmacies").select("id, is_active, state, created_at"),
      supabase.from("health_assessments").select("id, status, triage_level, created_at, user_id").order("created_at", { ascending: false }).limit(1000),
      supabase.from("prescriptions").select("id, status, peptide_name, created_at, sent_at").order("created_at", { ascending: false }).limit(1000),
      supabase.from("appointments").select("id, status, call_type, start_time, end_time, patient_id, created_at").order("created_at", { ascending: false }).limit(1000),
      supabase.from("salesperson_settings").select("user_id, is_active, commission_rate, monthly_target"),
      supabase.from("commission_payouts").select("id, amount_cents, status, period_start, salesperson_id").order("period_start", { ascending: false }).limit(500),
      supabase.from("messages").select("id, is_read, created_at, sender_id, recipient_id, conversation_id").order("created_at", { ascending: false }).limit(1000),
      supabase.from("provider_payouts").select("id, amount_cents, status, consultation_count, period_start, provider_id").order("period_start", { ascending: false }).limit(500),
      supabase.from("pharmacy_staff").select("id, pharmacy_id, user_id, role"),
      supabase.from("patient_progress_logs").select("id, metric_type, created_at, user_id").order("created_at", { ascending: false }).limit(1000),
      supabase.from("notification_preferences").select("id, marketing_emails, appointment_reminders, message_notifications").limit(1000),
      supabase.from("referral_clicks").select("id, referral_code, created_at").order("created_at", { ascending: false }).limit(1000),
    ]);

    setData({
      orders: orders || [],
      profiles: profiles || [],
      roles: roles || [],
      credentials: credentials || [],
      pharmacies: pharmacies || [],
      assessments: assessments || [],
      prescriptions: prescriptions || [],
      appointments: appointments || [],
      salesSettings: salesSettings || [],
      payouts: payouts || [],
      messages: messages || [],
      providerPayouts: providerPayouts || [],
      pharmacyStaff: pharmacyStaff || [],
      progressLogs: progressLogs || [],
      notifPrefs: notifPrefs || [],
      referralClicks: referralClicks || [],
    });
    setLastRefresh(new Date());
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(() => load(true), REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [load]);

  // Dynamic sparkline buckets based on selected date range
  const sparkBuckets = useMemo(() => {
    const n = new Date();
    const rangeFrom = dateRange.from ? dateRange.from.getTime() : n.getTime() - 8 * 7 * 86400000;
    const rangeTo = dateRange.to.getTime();
    const spanMs = rangeTo - rangeFrom;
    const DAY = 86400000;
    const WEEK = 7 * DAY;

    let count: number;
    let bucketMs: number;

    if (spanMs <= 10 * DAY) {
      count = Math.max(Math.ceil(spanMs / DAY), 2);
      bucketMs = DAY;
    } else if (spanMs <= 100 * DAY) {
      count = Math.max(Math.ceil(spanMs / WEEK), 2);
      bucketMs = WEEK;
    } else {
      count = 8;
      bucketMs = spanMs / count;
    }

    const buckets: { start: number; end: number; label: string }[] = [];
    for (let i = 0; i < count; i++) {
      const s = rangeTo - (count - i) * bucketMs;
      const e = rangeTo - (count - i - 1) * bucketMs;
      const sDate = new Date(s);
      const eDate = new Date(e);
      const label = isSameDay(sDate, eDate) || (e - s <= DAY)
        ? format(sDate, "MMM d")
        : `${format(sDate, "MMM d")} – ${format(eDate, "MMM d")}`;
      buckets.push({ start: s, end: e, label });
    }
    return buckets;
  }, [dateRange]);

  // ── KPI Change Alert (>50%) — fires on dashboard load ───────────────────────
  useEffect(() => {
    if (loading || !data || preset === "all") return;

    const now2 = new Date();
    const alertKey = `kpi-alert-${preset}-${format(now2, "yyyy-MM-dd")}`;
    if (alertSentRef.current === alertKey) return;

    const sentKey = `kpi_alert_sent_${alertKey}`;
    if (localStorage.getItem(sentKey)) {
      alertSentRef.current = alertKey;
      return;
    }

    // Compute previous range inline
    const dr = preset === "custom"
      ? { from: customFrom || null, to: customTo || now2 }
      : getPresetRange(preset);
    if (!dr.from) return;
    const spanMs = dr.to.getTime() - dr.from.getTime();
    const pr = { from: new Date(dr.from.getTime() - spanMs), to: new Date(dr.from.getTime()) };
    const inPrev = (dateStr: string) => {
      const d = new Date(dateStr);
      return d >= startOfDay(pr.from) && d < startOfDay(pr.to);
    };
    const inCur = (dateStr: string) => {
      const d = new Date(dateStr);
      return d >= startOfDay(dr.from!) && d <= endOfDay(dr.to);
    };
    const pct = (cur: number, prev: number): number | null => {
      if (prev === 0 && cur === 0) return 0;
      if (prev === 0) return 100;
      return ((cur - prev) / prev) * 100;
    };

    const { orders: ao, profiles: ap, assessments: aa, prescriptions: arx, appointments: aappt, referralClicks: arc } = data;

    const checks = [
      { cat: "Revenue", met: "Revenue", cur: ao.filter((o: any) => inCur(o.created_at) && !["cancelled","refunded"].includes(o.status)).reduce((s: number, o: any) => s + o.amount_cents, 0), prev: ao.filter((o: any) => inPrev(o.created_at) && !["cancelled","refunded"].includes(o.status)).reduce((s: number, o: any) => s + o.amount_cents, 0), isCents: true },
      { cat: "Users", met: "New Users", cur: ap.filter((p: any) => inCur(p.created_at)).length, prev: ap.filter((p: any) => inPrev(p.created_at)).length, isCents: false },
      { cat: "Assessments", met: "Total", cur: aa.filter((a: any) => inCur(a.created_at)).length, prev: aa.filter((a: any) => inPrev(a.created_at)).length, isCents: false },
      { cat: "Prescriptions", met: "Total", cur: arx.filter((p: any) => inCur(p.created_at)).length, prev: arx.filter((p: any) => inPrev(p.created_at)).length, isCents: false },
      { cat: "Appointments", met: "Total", cur: aappt.filter((a: any) => inCur(a.created_at)).length, prev: aappt.filter((a: any) => inPrev(a.created_at)).length, isCents: false },
      { cat: "Sales", met: "Referral Clicks", cur: (arc || []).filter((c: any) => inCur(c.created_at)).length, prev: (arc || []).filter((c: any) => inPrev(c.created_at)).length, isCents: false },
    ];

    const fmtVal = (v: number, isCents: boolean) => isCents ? fmtUsd(v) : fmt(v);
    const alerts = checks
      .map(c => {
        const change = pct(c.cur, c.prev);
        if (change == null || !isFinite(change) || Math.abs(change) <= alertThreshold) return null;
        return { category: c.cat, metric: c.met, current: fmtVal(c.cur, c.isCents), previous: fmtVal(c.prev, c.isCents), change: `${change > 0 ? "+" : ""}${change.toFixed(1)}%` };
      })
      .filter(Boolean) as { category: string; metric: string; current: string; previous: string; change: string }[];

    if (alerts.length === 0) return;

    const sendAlerts = async () => {
      const { data: adminRoles } = await supabase.from("user_roles").select("user_id").eq("role", "admin");
      if (!adminRoles?.length) return;
      const { data: adminEmails } = await supabase.rpc("get_user_emails");
      if (!adminEmails) return;
      const adminIds = new Set(adminRoles.map((r: any) => r.user_id));
      const emails = adminEmails.filter((e: any) => adminIds.has(e.user_id)).map((e: any) => e.email);
      const periodLabel = DATE_PRESETS.find(p => p.value === preset)?.label || preset;

      for (const email of emails) {
        await supabase.functions.invoke("send-transactional-email", {
          body: { templateName: "kpi-change-alert", recipientEmail: email, idempotencyKey: `kpi-alert-${email}-${alertKey}`, templateData: { period: periodLabel, alerts, threshold: alertThreshold } },
        });
      }

      localStorage.setItem(sentKey, "1");
      alertSentRef.current = alertKey;
      toast({ title: "KPI Alert Sent", description: `${alerts.length} significant KPI change${alerts.length === 1 ? "" : "s"} emailed to ${emails.length} admin${emails.length === 1 ? "" : "s"}.` });
    };

    sendAlerts();
  }, [loading, data, preset, customFrom, customTo, alertThreshold]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const { orders: allOrders, profiles: allProfiles, roles, credentials, pharmacies, assessments: allAssessments, prescriptions: allPrescriptions, appointments: allAppointments, salesSettings, payouts: allPayouts, messages: allMessages, providerPayouts: allProviderPayouts, pharmacyStaff, progressLogs: allProgressLogs, notifPrefs, referralClicks: allReferralClicks } = data;

  // Apply date range filter to time-series data
  const orders = allOrders.filter((o: any) => inRange(o.created_at));
  const profiles = allProfiles;
  const assessments = allAssessments.filter((a: any) => inRange(a.created_at));
  const prescriptions = allPrescriptions.filter((p: any) => inRange(p.created_at));
  const appointments = allAppointments.filter((a: any) => inRange(a.created_at));
  const payouts = allPayouts.filter((p: any) => inRange(p.period_start));
  const messages = allMessages.filter((m: any) => inRange(m.created_at));
  const providerPayouts = allProviderPayouts.filter((p: any) => inRange(p.period_start));
  const progressLogs = allProgressLogs.filter((l: any) => inRange(l.created_at));

  // Previous period data for comparison
  const prevOrders = allOrders.filter((o: any) => inPrevRange(o.created_at));
  const prevAssessments = allAssessments.filter((a: any) => inPrevRange(a.created_at));
  const prevPrescriptions = allPrescriptions.filter((p: any) => inPrevRange(p.created_at));
  const prevAppointments = allAppointments.filter((a: any) => inPrevRange(a.created_at));
  const prevNewUsers = allProfiles.filter((p: any) => inPrevRange(p.created_at)).length;
  const prevReferralClicks = allReferralClicks.filter((c: any) => inPrevRange(c.created_at));

  // ── Derived metrics ─────────────────────────────────
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 86400000);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 86400000);

  const sparkCount = (items: any[], dateKey: string, filter: (i: any) => boolean): SparkPoint[] =>
    sparkBuckets.map(b => ({
      v: items.filter(i => {
        const t = new Date(i[dateKey]).getTime();
        return t >= b.start && t < b.end && filter(i);
      }).length,
      label: b.label,
    }));

  const sparkSum = (items: any[], dateKey: string, sumKey: string, filter: (i: any) => boolean, divisor = 1): SparkPoint[] =>
    sparkBuckets.map(b => ({
      v: items.filter(i => {
        const t = new Date(i[dateKey]).getTime();
        return t >= b.start && t < b.end && filter(i);
      }).reduce((s: number, i: any) => s + (i[sumKey] || 0), 0) / divisor,
      label: b.label,
    }));

  const rangeLabel = preset === "all" ? "" : preset === "custom"
    ? `${customFrom ? format(customFrom, "MMM d") : "?"} – ${customTo ? format(customTo, "MMM d") : "now"}`
    : DATE_PRESETS.find(p => p.value === preset)?.label || "";

  // Users
  const totalUsers = allProfiles.length;
  const newUsersInRange = allProfiles.filter((p: any) => inRange(p.created_at)).length;
  const newUsers30d = allProfiles.filter((p: any) => new Date(p.created_at) >= thirtyDaysAgo).length;
  const newUsers7d = allProfiles.filter((p: any) => new Date(p.created_at) >= sevenDaysAgo).length;
  const roleCounts: Record<string, number> = {};
  roles.forEach((r: any) => { roleCounts[r.role] = (roleCounts[r.role] || 0) + 1; });
  const unassignedPatients = allProfiles.filter((p: any) => !p.assigned_provider_id).length;

  const userWeeklyTrends = {
    total: sparkCount(allProfiles, "created_at", () => true),
    unassigned: sparkCount(allProfiles, "created_at", (p: any) => !p.assigned_provider_id),
  };

  // User growth (last 6 months)
  const userGrowth: Record<string, number> = {};
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    userGrowth[d.toLocaleString("default", { month: "short", year: "2-digit" })] = 0;
  }
  allProfiles.filter((p: any) => inRange(p.created_at)).forEach((p: any) => {
    const d = new Date(p.created_at);
    const key = d.toLocaleString("default", { month: "short", year: "2-digit" });
    if (key in userGrowth) userGrowth[key]++;
  });
  const userGrowthData = Object.entries(userGrowth).map(([month, count]) => ({ month, count }));

  // Orders / Revenue
  const totalRevenue = orders.filter((o: any) => !["cancelled", "refunded"].includes(o.status)).reduce((s: number, o: any) => s + o.amount_cents, 0);
  const revenue30d = allOrders.filter((o: any) => new Date(o.created_at) >= thirtyDaysAgo && !["cancelled", "refunded"].includes(o.status)).reduce((s: number, o: any) => s + o.amount_cents, 0);
  const totalOrders = orders.length;
  const orders30d = allOrders.filter((o: any) => new Date(o.created_at) >= thirtyDaysAgo).length;
  const avgOrderValue = totalOrders > 0 ? totalRevenue / totalOrders : 0;
  const activeSubscriptions = orders.filter((o: any) => o.status === "active" && o.order_type === "subscription").length;
  const pendingOrders = orders.filter((o: any) => o.status === "pending").length;
  const cancelledOrders = orders.filter((o: any) => o.status === "cancelled").length;
  const refundedTotal = orders.filter((o: any) => o.status === "refunded").reduce((s: number, o: any) => s + o.amount_cents, 0);
  const mrr = activeSubscriptions > 0 ? orders.filter((o: any) => o.status === "active" && o.order_type === "subscription").reduce((s: number, o: any) => s + o.amount_cents, 0) : 0;

  const revenueWeeklyTrends = {
    revenue: sparkSum(orders, "created_at", "amount_cents", (o: any) => !["cancelled", "refunded"].includes(o.status), 100),
    orders: sparkCount(orders, "created_at", () => true),
    pending: sparkCount(orders, "created_at", (o: any) => o.status === "pending"),
    refunded: sparkSum(orders, "created_at", "amount_cents", (o: any) => o.status === "refunded", 100),
  };

  // ── Previous period comparison values ─────────────────────
  const prevRevenue = prevOrders.filter((o: any) => !["cancelled", "refunded"].includes(o.status)).reduce((s: number, o: any) => s + o.amount_cents, 0);
  const prevOrderCount = prevOrders.length;
  const prevPendingOrders = prevOrders.filter((o: any) => o.status === "pending").length;
  const prevRefundedTotal = prevOrders.filter((o: any) => o.status === "refunded").reduce((s: number, o: any) => s + o.amount_cents, 0);
  const prevTotalAssessments = prevAssessments.length;
  const prevPendingAssessments = prevAssessments.filter((a: any) => a.status === "pending").length;
  const prevTotalPrescriptions = prevPrescriptions.length;
  const prevSentPrescriptions = prevPrescriptions.filter((p: any) => p.sent_at).length;
  const prevTotalAppointments = prevAppointments.length;
  const prevCompletedAppointments = prevAppointments.filter((a: any) => a.status === "completed").length;
  const prevNoShowCount = prevAppointments.filter((a: any) => a.status === "no_show").length;
  const prevReferredOrders = prevOrders.filter((o: any) => o.salesperson_id).length;
  const prevReferralRevenue = prevOrders.filter((o: any) => o.salesperson_id && !["cancelled", "refunded"].includes(o.status)).reduce((s: number, o: any) => s + o.amount_cents, 0);
  const prevTotalReferralClicks = prevReferralClicks.length;

  // Monthly revenue
  const monthlyRevenue: Record<string, number> = {};
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthlyRevenue[d.toLocaleString("default", { month: "short", year: "2-digit" })] = 0;
  }
  orders.filter((o: any) => !["cancelled", "refunded"].includes(o.status)).forEach((o: any) => {
    const d = new Date(o.created_at);
    const key = d.toLocaleString("default", { month: "short", year: "2-digit" });
    if (key in monthlyRevenue) monthlyRevenue[key] += o.amount_cents;
  });
  const revenueChartData = Object.entries(monthlyRevenue).map(([month, cents]) => ({ month, revenue: cents / 100 }));

  // Order status breakdown
  const orderStatusCounts: Record<string, number> = {};
  orders.forEach((o: any) => { orderStatusCounts[o.status] = (orderStatusCounts[o.status] || 0) + 1; });
  const orderPieData = Object.entries(orderStatusCounts).map(([name, value]) => ({ name, value }));

  // Providers
  const totalProviders = roleCounts["provider"] || 0;
  const pendingCredentials = credentials.filter((c: any) => c.credential_status === "pending_review").length;
  const approvedCredentials = credentials.filter((c: any) => c.credential_status === "approved").length;
  const rejectedCredentials = credentials.filter((c: any) => c.credential_status === "rejected").length;
  const expiringLicenses = credentials.filter((c: any) => {
    if (!c.license_expiry) return false;
    const exp = new Date(c.license_expiry);
    return exp <= new Date(now.getTime() + 90 * 86400000) && exp >= now;
  }).length;
  const specialtyCounts: Record<string, number> = {};
  credentials.forEach((c: any) => {
    const s = c.specialty || "Unspecified";
    specialtyCounts[s] = (specialtyCounts[s] || 0) + 1;
  });
  const specialtyData = Object.entries(specialtyCounts).map(([name, value]) => ({ name, value }));

  const providerWeeklyTrends = {
    approved: sparkCount(credentials, "created_at", (c: any) => c.credential_status === "approved"),
    pending: sparkCount(credentials, "created_at", (c: any) => c.credential_status === "pending_review"),
    rejected: sparkCount(credentials, "created_at", (c: any) => c.credential_status === "rejected"),
  };

  // Pharmacies
  const totalPharmacies = pharmacies.length;
  const activePharmacies = pharmacies.filter((p: any) => p.is_active).length;
  const pharmacyStates = new Set(pharmacies.map((p: any) => p.state).filter(Boolean)).size;

  const pharmacyWeeklyTrends = {
    total: sparkCount(pharmacies, "created_at", () => true),
  };

  // Assessments
  const totalAssessments = assessments.length;
  const pendingAssessments = assessments.filter((a: any) => a.status === "pending").length;
  const reviewedAssessments = assessments.filter((a: any) => a.status === "reviewed").length;
  const triageCounts: Record<string, number> = {};
  assessments.forEach((a: any) => {
    const t = a.triage_level || "unset";
    triageCounts[t] = (triageCounts[t] || 0) + 1;
  });
  const triageData = Object.entries(triageCounts).map(([name, value]) => ({ name, value }));
  const conversionRate = totalAssessments > 0 ? ((reviewedAssessments / totalAssessments) * 100).toFixed(1) : "0";

  // Prescriptions
  const totalPrescriptions = prescriptions.length;
  const draftPrescriptions = prescriptions.filter((p: any) => p.status === "draft").length;
  const sentPrescriptions = prescriptions.filter((p: any) => p.sent_at).length;
  const peptideCounts: Record<string, number> = {};
  prescriptions.forEach((p: any) => {
    peptideCounts[p.peptide_name] = (peptideCounts[p.peptide_name] || 0) + 1;
  });
  const topPeptides = Object.entries(peptideCounts).sort(([, a], [, b]) => b - a).slice(0, 6).map(([name, value]) => ({ name, value }));

  const assessRxWeeklyTrends = {
    assessments: sparkCount(assessments, "created_at", () => true),
    pending: sparkCount(assessments, "created_at", (a: any) => a.status === "pending"),
    prescriptions: sparkCount(prescriptions, "created_at", () => true),
    sent: sparkCount(prescriptions, "created_at", (p: any) => !!p.sent_at),
  };

  // Appointments
  const totalAppointments = appointments.length;
  const scheduledAppointments = appointments.filter((a: any) => a.status === "scheduled").length;
  const completedAppointments = appointments.filter((a: any) => a.status === "completed").length;
  const upcomingAppointments = appointments.filter((a: any) => new Date(a.start_time) >= now && (a.status === "scheduled" || a.status === "confirmed")).length;
  const rescheduledAppointments = appointments.filter((a: any) => a.status === "rescheduled").length;
  // Patients stuck mid-reschedule: status='rescheduled' with no active future booking.
  // Computed across ALL appointments (not date-filtered) to reflect current operational backlog.
  const patientsWithActiveBooking = new Set(
    allAppointments
      .filter((a: any) => (a.status === "scheduled" || a.status === "confirmed") && new Date(a.start_time) >= now)
      .map((a: any) => a.patient_id)
  );
  const awaitingRescheduleCount = allAppointments.filter(
    (a: any) => a.status === "rescheduled" && !patientsWithActiveBooking.has(a.patient_id)
  ).length;
  const cancelledAppointments = appointments.filter((a: any) => a.status === "cancelled").length;
  const confirmedAppointments = appointments.filter((a: any) => a.status === "confirmed").length;
  const noShowCount = appointments.filter((a: any) => a.status === "no_show").length;
  const noShowRate = totalAppointments > 0 ? ((noShowCount / totalAppointments) * 100).toFixed(1) : "0";
  const rescheduleRate = totalAppointments > 0 ? ((rescheduledAppointments / totalAppointments) * 100).toFixed(1) : "0";
  const cancelledRate = totalAppointments > 0 ? ((cancelledAppointments / totalAppointments) * 100).toFixed(1) : "0";
  const confirmedRate = totalAppointments > 0 ? ((confirmedAppointments / totalAppointments) * 100).toFixed(1) : "0";
  const completionRate = totalAppointments > 0 ? ((completedAppointments / totalAppointments) * 100).toFixed(1) : "0";

  const apptWeeklyTrends = {
    total: sparkCount(appointments, "created_at", () => true),
    scheduled: sparkCount(appointments, "created_at", (a: any) => a.status === "scheduled"),
    upcoming: sparkCount(appointments, "created_at", (a: any) => new Date(a.start_time) >= now && (a.status === "scheduled" || a.status === "confirmed")),
    confirmed: sparkCount(appointments, "created_at", (a: any) => a.status === "confirmed"),
    completed: sparkCount(appointments, "created_at", (a: any) => a.status === "completed"),
    cancelled: sparkCount(appointments, "created_at", (a: any) => a.status === "cancelled"),
    rescheduled: sparkCount(appointments, "created_at", (a: any) => a.status === "rescheduled"),
    noShow: sparkCount(appointments, "created_at", (a: any) => a.status === "no_show"),
  };

  // Sales
  const totalSalespeople = roleCounts["salesperson"] || 0;
  const activeSalespeople = salesSettings.filter((s: any) => s.is_active).length;
  const totalCommissionsPaid = payouts.filter((p: any) => p.status === "paid").reduce((s: number, p: any) => s + p.amount_cents, 0);
  const pendingPayouts = payouts.filter((p: any) => p.status === "pending").reduce((s: number, p: any) => s + p.amount_cents, 0);
  const referredOrders = orders.filter((o: any) => o.salesperson_id).length;
  const referralRevenue = orders.filter((o: any) => o.salesperson_id && !["cancelled", "refunded"].includes(o.status)).reduce((s: number, o: any) => s + o.amount_cents, 0);
  const referralRate = totalOrders > 0 ? ((referredOrders / totalOrders) * 100).toFixed(1) : "0";

  // Referral Clicks
  const referralClicksFiltered = allReferralClicks.filter((c: any) => inRange(c.created_at));
  const totalReferralClicks = referralClicksFiltered.length;
  const uniqueReferralCodes = new Set(referralClicksFiltered.map((c: any) => c.referral_code)).size;
  const clickToOrderRate = totalReferralClicks > 0 ? ((referredOrders / totalReferralClicks) * 100).toFixed(1) : "0";

  const salesWeeklyTrends = {
    clicks: sparkCount(referralClicksFiltered, "created_at", () => true),
    referralOrders: sparkCount(orders, "created_at", (o: any) => !!o.salesperson_id),
    referralRevenue: sparkSum(orders, "created_at", "amount_cents", (o: any) => !!o.salesperson_id && !["cancelled", "refunded"].includes(o.status), 100),
    commissionsPaid: sparkSum(payouts, "period_start", "amount_cents", (p: any) => p.status === "paid", 100),
    pendingPayouts: sparkSum(payouts, "period_start", "amount_cents", (p: any) => p.status === "pending", 100),
  };

  // Referral clicks by month (last 6 months)
  const monthlyClicks: Record<string, number> = {};
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    monthlyClicks[d.toLocaleString("default", { month: "short", year: "2-digit" })] = 0;
  }
  referralClicksFiltered.forEach((c: any) => {
    const d = new Date(c.created_at);
    const key = d.toLocaleString("default", { month: "short", year: "2-digit" });
    if (key in monthlyClicks) monthlyClicks[key]++;
  });
  const clickChartData = Object.entries(monthlyClicks).map(([month, clicks]) => ({ month, clicks }));

  // Messages
  const totalMessages = messages.length;
  const unreadMessages = allMessages.filter((m: any) => !m.is_read).length;
  const uniqueConversations = new Set(messages.map((m: any) => m.conversation_id)).size;
  const uniqueMessagers = new Set(messages.map((m: any) => m.sender_id)).size;

  // Provider Payouts
  const totalProviderPayoutsPaid = providerPayouts.filter((p: any) => p.status === "paid").reduce((s: number, p: any) => s + p.amount_cents, 0);
  const pendingProviderPayouts = providerPayouts.filter((p: any) => p.status === "pending").reduce((s: number, p: any) => s + p.amount_cents, 0);
  const totalConsultationsPaid = providerPayouts.filter((p: any) => p.status === "paid").reduce((s: number, p: any) => s + p.consultation_count, 0);

  // Prescription Pipeline (full lifecycle)
  const rxPipeline: Record<string, number> = { draft: 0, sent: 0, received: 0, filling: 0, filled: 0, shipped: 0, delivered: 0 };
  allPrescriptions.forEach((p: any) => {
    if (p.status in rxPipeline) rxPipeline[p.status]++;
    else rxPipeline[p.status] = (rxPipeline[p.status] || 0) + 1;
  });
  const rxPipelineData = Object.entries(rxPipeline).filter(([, v]) => v > 0).map(([name, value]) => ({ name, value }));

  // Pharmacy Staff
  const totalPharmacyStaff = pharmacyStaff.length;
  const pharmacyUsers = roleCounts["pharmacy"] || 0;

  // Patient Engagement
  const totalProgressEntries = progressLogs.length;
  const uniqueTrackers = new Set(progressLogs.map((l: any) => l.user_id)).size;
  const metricTypes: Record<string, number> = {};
  progressLogs.forEach((l: any) => { metricTypes[l.metric_type] = (metricTypes[l.metric_type] || 0) + 1; });
  const topMetrics = Object.entries(metricTypes).sort(([, a], [, b]) => b - a).slice(0, 5);

  // Notification preferences
  const marketingOptIn = notifPrefs.filter((n: any) => n.marketing_emails).length;
  const totalWithPrefs = notifPrefs.length;

  // Top states
  const stateCounts: Record<string, number> = {};
  allProfiles.filter((p: any) => inRange(p.created_at)).forEach((p: any) => {
    if (p.state) stateCounts[p.state] = (stateCounts[p.state] || 0) + 1;
  });
  const topStates = Object.entries(stateCounts).sort(([, a], [, b]) => b - a).slice(0, 5);

  const exportCsv = () => {
    const rows: string[][] = [
      ["Category", "Metric", "Value", "Change %", "Previous Period Value"],
      ["Revenue", `Revenue (${rangeLabel || "All time"})`, fmtUsd(totalRevenue), pctChange(totalRevenue, prevRevenue)?.toFixed(1) ?? "N/A", fmtUsd(prevRevenue)],
      ["Revenue", "Revenue (30d)", fmtUsd(revenue30d), "", ""],
      ["Revenue", "MRR", fmtUsd(mrr), "", ""],
      ["Revenue", "Avg Order Value", fmtUsd(avgOrderValue), "", ""],
      ["Revenue", "Refunded", fmtUsd(refundedTotal), pctChange(refundedTotal, prevRefundedTotal)?.toFixed(1) ?? "N/A", fmtUsd(prevRefundedTotal)],
      ["Revenue", "Pending Orders", String(pendingOrders), pctChange(pendingOrders, prevPendingOrders)?.toFixed(1) ?? "N/A", String(prevPendingOrders)],
      ["Users", "Total Users", String(totalUsers), "", ""],
      ["Users", `New (${rangeLabel || "All time"})`, String(newUsersInRange), pctChange(newUsersInRange, prevNewUsers)?.toFixed(1) ?? "N/A", String(prevNewUsers)],
      ["Users", "Admins", String(roleCounts["admin"] || 0), "", ""],
      ["Users", "Providers", String(totalProviders), "", ""],
      ["Users", "Salespeople", String(totalSalespeople), "", ""],
      ["Users", "Unassigned Patients", String(unassignedPatients), "", ""],
      ["Assessments", "Total Assessments", String(totalAssessments), pctChange(totalAssessments, prevTotalAssessments)?.toFixed(1) ?? "N/A", String(prevTotalAssessments)],
      ["Assessments", "Pending", String(pendingAssessments), pctChange(pendingAssessments, prevPendingAssessments)?.toFixed(1) ?? "N/A", String(prevPendingAssessments)],
      ["Assessments", "Reviewed", String(reviewedAssessments), "", ""],
      ["Assessments", "Conversion Rate", `${conversionRate}%`, "", ""],
      ["Prescriptions", "Total Prescriptions", String(totalPrescriptions), pctChange(totalPrescriptions, prevTotalPrescriptions)?.toFixed(1) ?? "N/A", String(prevTotalPrescriptions)],
      ["Prescriptions", "Draft", String(draftPrescriptions), "", ""],
      ["Prescriptions", "Sent", String(sentPrescriptions), pctChange(sentPrescriptions, prevSentPrescriptions)?.toFixed(1) ?? "N/A", String(prevSentPrescriptions)],
      ["Appointments", "Total Appointments", String(totalAppointments), pctChange(totalAppointments, prevTotalAppointments)?.toFixed(1) ?? "N/A", String(prevTotalAppointments)],
      ["Appointments", "Completed", String(completedAppointments), pctChange(completedAppointments, prevCompletedAppointments)?.toFixed(1) ?? "N/A", String(prevCompletedAppointments)],
      ["Appointments", "No-Show", String(noShowCount), pctChange(noShowCount, prevNoShowCount)?.toFixed(1) ?? "N/A", String(prevNoShowCount)],
      ["Appointments", "Completion Rate", `${completionRate}%`, "", ""],
      ["Sales", "Referral Clicks", String(totalReferralClicks), pctChange(totalReferralClicks, prevTotalReferralClicks)?.toFixed(1) ?? "N/A", String(prevTotalReferralClicks)],
      ["Sales", "Referred Orders", String(referredOrders), pctChange(referredOrders, prevReferredOrders)?.toFixed(1) ?? "N/A", String(prevReferredOrders)],
      ["Sales", "Referral Revenue", fmtUsd(referralRevenue), pctChange(referralRevenue, prevReferralRevenue)?.toFixed(1) ?? "N/A", fmtUsd(prevReferralRevenue)],
      ["Sales", "Total Commissions Paid", fmtUsd(totalCommissionsPaid), "", ""],
      ["Pharmacies", "Total Pharmacies", String(totalPharmacies), "", ""],
      ["Pharmacies", "Active", String(activePharmacies), "", ""],
      ["Messages", "Total Messages", String(totalMessages), "", ""],
      ["Messages", "Unread", String(unreadMessages), "", ""],
    ];
    const csv = rows.map(r => r.map(c => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `elara-kpi-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  return (
    <div className="space-y-8">
      {/* Date range filter + auto-refresh */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-1.5">
          {DATE_PRESETS.map((p) => (
            <Button
              key={p.value}
              variant={preset === p.value ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => setPreset(p.value)}
            >
              {p.label}
            </Button>
          ))}
          {preset === "custom" && (
            <div className="flex items-center gap-1.5 ml-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className={cn("h-7 text-xs gap-1", !customFrom && "text-muted-foreground")}>
                    <CalendarIcon size={12} />
                    {customFrom ? format(customFrom, "MMM d, yyyy") : "From"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customFrom} onSelect={setCustomFrom} initialFocus className="p-3 pointer-events-auto" />
                </PopoverContent>
              </Popover>
              <span className="text-xs text-muted-foreground">→</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className={cn("h-7 text-xs gap-1", !customTo && "text-muted-foreground")}>
                    <CalendarIcon size={12} />
                    {customTo ? format(customTo, "MMM d, yyyy") : "To"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={customTo} onSelect={setCustomTo} initialFocus className="p-3 pointer-events-auto" />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {refreshing && (
            <div className="w-3 h-3 border-[1.5px] border-primary border-t-transparent rounded-full animate-spin" />
          )}
          <span>Updated {lastRefresh.toLocaleTimeString()}</span>
          <button onClick={() => load(true)} disabled={refreshing} className="text-primary hover:underline disabled:opacity-50">
            Refresh
          </button>
          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={exportCsv}>
            <Download size={12} /> Export CSV
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1">
                <Settings size={12} /> Alert: {alertThreshold}%
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-3" align="end">
              <div className="space-y-2">
                <Label className="text-xs font-medium">KPI Alert Threshold</Label>
                <p className="text-[11px] text-muted-foreground">
                  Email admins when a KPI changes by more than this percentage.
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={500}
                    value={alertThreshold}
                    onChange={(e) => handleThresholdChange(e.target.value)}
                    className="h-7 text-xs"
                  />
                  <span className="text-xs text-muted-foreground">%</span>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {/* ── Revenue & Payments ───────────────────────── */}
      <div>
        <SectionTitle icon={DollarSign}>Revenue &amp; Payments</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-3">
          <KPICard label={preset === "all" ? "Total Revenue" : `Revenue (${rangeLabel})`} value={fmtUsd(totalRevenue)} icon={DollarSign} sub={`${totalOrders} orders`} onClick={() => nav("orders")} sparkData={revenueWeeklyTrends.revenue} change={pctChange(totalRevenue, prevRevenue)} prevValue={fmtUsd(prevRevenue)} />
          <KPICard label="Revenue (30d)" value={fmtUsd(revenue30d)} icon={TrendingUp} sub={`${orders30d} orders`} onClick={() => nav("orders")} sparkData={revenueWeeklyTrends.orders} />
          <KPICard label="MRR" value={fmtUsd(mrr)} icon={CreditCard} sub={`${activeSubscriptions} active subs`} onClick={() => nav("orders")} />
          <KPICard label="Avg Order Value" value={fmtUsd(avgOrderValue)} icon={ShoppingCart} onClick={() => nav("orders")} />
          <KPICard label="Refunded" value={fmtUsd(refundedTotal)} icon={RotateCcw} color="text-destructive" onClick={() => nav("orders")} sparkData={revenueWeeklyTrends.refunded} sparkColor="hsl(var(--destructive))" change={pctChange(refundedTotal, prevRefundedTotal)} prevValue={fmtUsd(prevRefundedTotal)} />
          <KPICard label="Pending Orders" value={fmt(pendingOrders)} icon={Clock} color="text-champagne" onClick={() => nav("orders")} sparkData={revenueWeeklyTrends.pending} sparkColor="hsl(var(--champagne))" change={pctChange(pendingOrders, prevPendingOrders)} prevValue={fmt(prevPendingOrders)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Monthly Revenue</CardTitle></CardHeader>
            <CardContent>
              {revenueChartData.every(d => d.revenue === 0) ? (
                <p className="text-center text-muted-foreground py-8 text-sm">No revenue data yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={revenueChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={v => `$${v}`} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} formatter={(v: number) => [`$${v.toFixed(2)}`, "Revenue"]} />
                    <Area type="monotone" dataKey="revenue" fill="hsl(var(--primary) / 0.15)" stroke="hsl(var(--primary))" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Order Status</CardTitle></CardHeader>
            <CardContent>
              {orderPieData.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">No orders yet</p>
              ) : (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="50%" height={180}>
                    <PieChart>
                      <Pie data={orderPieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35}>
                        {orderPieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5">
                    {orderPieData.map((d, i) => (
                      <div key={d.name} className="flex items-center gap-2 text-xs">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="capitalize">{d.name}</span>
                        <Badge variant="secondary" className="text-[10px] ml-auto">{d.value}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Users & Growth ───────────────────────── */}
      <div>
        <SectionTitle icon={Users}>Users &amp; Growth</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-3">
          <KPICard label="Total Users" value={fmt(totalUsers)} icon={Users} onClick={() => nav("users")} sparkData={userWeeklyTrends.total} />
          <KPICard label={preset === "all" ? "New (30d)" : `New (${rangeLabel})`} value={fmt(preset === "all" ? newUsers30d : newUsersInRange)} icon={UserPlus} sub={`${newUsers7d} this week`} onClick={() => nav("users")} sparkData={userWeeklyTrends.total} change={pctChange(newUsersInRange, prevNewUsers)} prevValue={fmt(prevNewUsers)} />
          <KPICard label="Admins" value={fmt(roleCounts["admin"] || 0)} icon={Activity} onClick={() => nav("users")} />
          <KPICard label="Providers" value={fmt(totalProviders)} icon={Stethoscope} onClick={() => nav("providers")} />
          <KPICard label="Salespeople" value={fmt(totalSalespeople)} icon={Briefcase} onClick={() => nav("sales")} />
          <KPICard label="Unassigned Patients" value={fmt(unassignedPatients)} icon={AlertTriangle} color="text-champagne" onClick={() => nav("patients")} sparkData={userWeeklyTrends.unassigned} sparkColor="hsl(var(--champagne))" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">User Signups (6 months)</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={userGrowthData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Top States</CardTitle></CardHeader>
            <CardContent>
              {topStates.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">No state data</p>
              ) : (
                <div className="space-y-3 pt-2">
                  {topStates.map(([state, count]) => (
                    <div key={state} className="flex items-center gap-3">
                      <span className="text-sm font-medium w-8">{state}</span>
                      <div className="flex-1 bg-muted rounded-full h-2.5">
                        <div className="bg-primary rounded-full h-2.5 transition-all" style={{ width: `${(count / topStates[0][1]) * 100}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground w-8 text-right">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Providers ───────────────────────── */}
      <div>
        <SectionTitle icon={Stethoscope}>Providers</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mt-3">
          <KPICard label="Active Providers" value={fmt(approvedCredentials)} icon={CheckCircle} onClick={() => nav("providers")} sparkData={providerWeeklyTrends.approved} />
          <KPICard label="Pending Review" value={fmt(pendingCredentials)} icon={Clock} color="text-champagne" onClick={() => nav("providers")} sparkData={providerWeeklyTrends.pending} sparkColor="hsl(var(--champagne))" />
          <KPICard label="Rejected" value={fmt(rejectedCredentials)} icon={XCircle} color="text-destructive" onClick={() => nav("providers")} sparkData={providerWeeklyTrends.rejected} sparkColor="hsl(var(--destructive))" />
          <KPICard label="Expiring Licenses (90d)" value={fmt(expiringLicenses)} icon={AlertTriangle} color={expiringLicenses > 0 ? "text-destructive" : "text-muted-foreground"} onClick={() => nav("providers")} />
          <KPICard label="Specialties" value={fmt(Object.keys(specialtyCounts).length)} icon={ClipboardList} onClick={() => nav("providers")} />
        </div>
        {specialtyData.length > 0 && (
          <Card className="border-border mt-4">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Provider Specialties</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={specialtyData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={120} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="value" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Partner Pharmacies ───────────────────────── */}
      <div>
        <SectionTitle icon={Building2}>Partner Pharmacies</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <KPICard label="Total Pharmacies" value={fmt(totalPharmacies)} icon={Building2} onClick={() => nav("pharmacies")} sparkData={pharmacyWeeklyTrends.total} />
          <KPICard label="Active" value={fmt(activePharmacies)} icon={CheckCircle} sub={`${totalPharmacies - activePharmacies} inactive`} onClick={() => nav("pharmacies")} />
          <KPICard label="States Covered" value={fmt(pharmacyStates)} icon={Activity} onClick={() => nav("pharmacies")} />
          <KPICard label="Staff Members" value={fmt(totalPharmacyStaff)} icon={UserCog} sub={`${pharmacyUsers} pharmacy users`} onClick={() => nav("pharmacies")} />
        </div>
      </div>

      {/* ── Assessments & Prescriptions ───────────────────────── */}
      <div>
        <SectionTitle icon={FileText}>Assessments &amp; Prescriptions</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mt-3">
          <KPICard label="Total Assessments" value={fmt(totalAssessments)} icon={FileText} onClick={() => nav("patients")} sparkData={assessRxWeeklyTrends.assessments} change={pctChange(totalAssessments, prevTotalAssessments)} prevValue={fmt(prevTotalAssessments)} />
          <KPICard label="Pending Review" value={fmt(pendingAssessments)} icon={Clock} color="text-champagne" onClick={() => nav("patients")} sparkData={assessRxWeeklyTrends.pending} sparkColor="hsl(var(--champagne))" change={pctChange(pendingAssessments, prevPendingAssessments)} prevValue={fmt(prevPendingAssessments)} />
          <KPICard label={`Assessments${rangeLabel ? ` (${rangeLabel})` : ""}`} value={fmt(totalAssessments)} icon={TrendingUp} onClick={() => nav("patients")} />
          <KPICard label="Completion Rate" value={`${conversionRate}%`} icon={CheckCircle} onClick={() => nav("patients")} />
          <KPICard label="Total Prescriptions" value={fmt(totalPrescriptions)} icon={Pill} onClick={() => nav("orders")} sparkData={assessRxWeeklyTrends.prescriptions} change={pctChange(totalPrescriptions, prevTotalPrescriptions)} prevValue={fmt(prevTotalPrescriptions)} />
          <KPICard label="Sent to Pharmacy" value={fmt(sentPrescriptions)} icon={CheckCircle} sub={`${draftPrescriptions} drafts`} onClick={() => nav("pharmacies")} sparkData={assessRxWeeklyTrends.sent} change={pctChange(sentPrescriptions, prevSentPrescriptions)} prevValue={fmt(prevSentPrescriptions)} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Triage Levels</CardTitle></CardHeader>
            <CardContent>
              {triageData.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">No triage data</p>
              ) : (
                <div className="flex items-center gap-4">
                  <ResponsiveContainer width="50%" height={160}>
                    <PieChart>
                      <Pie data={triageData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
                        {triageData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-1.5">
                    {triageData.map((d, i) => (
                      <div key={d.name} className="flex items-center gap-2 text-xs">
                        <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="capitalize">{d.name}</span>
                        <Badge variant="secondary" className="text-[10px] ml-auto">{d.value}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Top Peptides Prescribed</CardTitle></CardHeader>
            <CardContent>
              {topPeptides.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">No prescriptions yet</p>
              ) : (
                <div className="space-y-2.5 pt-1">
                  {topPeptides.map(({ name, value }) => (
                    <div key={name} className="flex items-center gap-3">
                      <span className="text-xs font-medium truncate w-28">{name}</span>
                      <div className="flex-1 bg-muted rounded-full h-2">
                        <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${(value / topPeptides[0].value) * 100}%` }} />
                      </div>
                      <span className="text-xs text-muted-foreground w-6 text-right">{value}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Appointments ───────────────────────── */}
      <div>
        <SectionTitle icon={CalendarCheck}>Appointments</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-9 gap-3 mt-3">
          <KPICard label="Total Appointments" value={fmt(totalAppointments)} icon={CalendarCheck} onClick={() => nav("patients")} sparkData={apptWeeklyTrends.total} change={pctChange(totalAppointments, prevTotalAppointments)} prevValue={fmt(prevTotalAppointments)} />
          <KPICard label="Scheduled" value={fmt(scheduledAppointments)} icon={CalendarIcon} color="text-primary" sub={`${totalAppointments > 0 ? ((scheduledAppointments / totalAppointments) * 100).toFixed(1) : "0"}% of total`} onClick={() => nav("patients")} sparkData={apptWeeklyTrends.scheduled} />
          <KPICard label="Upcoming" value={fmt(upcomingAppointments)} icon={Clock} color="text-champagne" onClick={() => nav("patients")} sparkData={apptWeeklyTrends.upcoming} sparkColor="hsl(var(--champagne))" />
          <KPICard label="Confirmed" value={fmt(confirmedAppointments)} icon={ShieldCheck} color="text-emerald-600" sub={`${confirmedRate}% rate`} onClick={() => nav("patients")} sparkData={apptWeeklyTrends.confirmed} sparkColor="#059669" />
          <KPICard label="Completed" value={fmt(completedAppointments)} icon={CheckCircle} sub={`${completionRate}% rate`} onClick={() => nav("patients")} sparkData={apptWeeklyTrends.completed} change={pctChange(completedAppointments, prevCompletedAppointments)} prevValue={fmt(prevCompletedAppointments)} />
          <KPICard label="Cancelled" value={fmt(cancelledAppointments)} icon={XCircle} color="text-muted-foreground" sub={`${cancelledRate}% rate`} onClick={() => nav("patients")} sparkData={apptWeeklyTrends.cancelled} sparkColor="hsl(var(--muted-foreground))" />
          <KPICard label="Rescheduled" value={fmt(rescheduledAppointments)} icon={RotateCcw} color="text-blue-600" sub={`${rescheduleRate}% rate`} onClick={() => nav("patients")} sparkData={apptWeeklyTrends.rescheduled} sparkColor="#2563eb" />
          <KPICard label="Awaiting Reschedule" value={fmt(awaitingRescheduleCount)} icon={AlertTriangle} color={awaitingRescheduleCount > 0 ? "text-amber-600" : "text-muted-foreground"} sub="No new slot picked" onClick={() => nav("patients")} />
          <KPICard label="No-Show" value={fmt(noShowCount)} icon={XCircle} color={parseFloat(noShowRate) > 10 ? "text-destructive" : "text-muted-foreground"} sub={`${noShowRate}% rate`} onClick={() => nav("patients")} sparkData={apptWeeklyTrends.noShow} sparkColor="hsl(var(--destructive))" change={pctChange(noShowCount, prevNoShowCount)} prevValue={fmt(prevNoShowCount)} />
        </div>
        {/* Weekly Appointments Trend Chart */}
        <Card className="border-border mt-4">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Weekly Appointments Trend (8 weeks)</CardTitle></CardHeader>
          <CardContent>
            {(() => {
              const chartData = apptWeeklyTrends.total.map((_, i) => {
                const wEnd = new Date(now.getTime() - (7 - i) * 7 * 86400000);
                return {
                  week: format(wEnd, "MMM d"),
                  Scheduled: apptWeeklyTrends.scheduled[i].v,
                  Confirmed: apptWeeklyTrends.confirmed[i].v,
                  Completed: apptWeeklyTrends.completed[i].v,
                  Cancelled: apptWeeklyTrends.cancelled[i].v,
                  Rescheduled: apptWeeklyTrends.rescheduled[i].v,
                  "No-Show": apptWeeklyTrends.noShow[i].v,
                };
              });
              const allZero = chartData.every(d => d.Scheduled + d.Confirmed + d.Completed + d.Cancelled + d.Rescheduled + d["No-Show"] === 0);
              if (allZero) return <p className="text-center text-muted-foreground py-8 text-sm">No appointment data yet</p>;
              return (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="week" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="line" iconSize={14} />
                    <Line type="monotone" dataKey="Completed" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Scheduled" stroke="#94a3b8" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Confirmed" stroke="#059669" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Cancelled" stroke="hsl(var(--muted-foreground))" strokeWidth={1.5} strokeDasharray="4 2" dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="Rescheduled" stroke="#2563eb" strokeWidth={1.5} strokeDasharray="4 2" dot={{ r: 2 }} />
                    <Line type="monotone" dataKey="No-Show" stroke="hsl(var(--destructive))" strokeWidth={1.5} strokeDasharray="4 2" dot={{ r: 2 }} />
                  </LineChart>
                </ResponsiveContainer>
              );
            })()}
          </CardContent>
        </Card>
        <div className="mt-4">
          <OverdueConsultationWidget />
        </div>
      </div>

      {/* ── Sales & Commissions ───────────────────────── */}
      <div>
        <SectionTitle icon={Briefcase}>Sales &amp; Commissions</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mt-3">
          <KPICard label="Active Salespeople" value={fmt(activeSalespeople)} icon={Briefcase} sub={`${totalSalespeople} total`} />
          <KPICard label="Referral Clicks" value={fmt(totalReferralClicks)} icon={MousePointerClick} sub={`${uniqueReferralCodes} unique codes`} sparkData={salesWeeklyTrends.clicks} change={pctChange(totalReferralClicks, prevTotalReferralClicks)} prevValue={fmt(prevTotalReferralClicks)} />
          <KPICard label="Click → Order" value={`${clickToOrderRate}%`} icon={TrendingUp} sub={`${referredOrders} conversions`} />
          <KPICard label="Referral Orders" value={fmt(referredOrders)} icon={ShoppingCart} sub={`${referralRate}% of all`} sparkData={salesWeeklyTrends.referralOrders} change={pctChange(referredOrders, prevReferredOrders)} prevValue={fmt(prevReferredOrders)} />
          <KPICard label="Referral Revenue" value={fmtUsd(referralRevenue)} icon={DollarSign} sparkData={salesWeeklyTrends.referralRevenue} change={pctChange(referralRevenue, prevReferralRevenue)} prevValue={fmtUsd(prevReferralRevenue)} />
          <KPICard label="Commissions Paid" value={fmtUsd(totalCommissionsPaid)} icon={CreditCard} sparkData={salesWeeklyTrends.commissionsPaid} />
          <KPICard label="Pending Payouts" value={fmtUsd(pendingPayouts)} icon={Clock} color="text-champagne" sparkData={salesWeeklyTrends.pendingPayouts} sparkColor="hsl(var(--champagne))" />
          <KPICard label="Cancelled Orders" value={fmt(cancelledOrders)} icon={XCircle} color="text-destructive" sparkData={revenueWeeklyTrends.refunded} sparkColor="hsl(var(--destructive))" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Referral Link Clicks (6 months)</CardTitle></CardHeader>
            <CardContent>
              {clickChartData.every(d => d.clicks === 0) ? (
                <p className="text-center text-muted-foreground py-8 text-sm">No referral clicks yet</p>
              ) : (
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={clickChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                    <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                    <Bar dataKey="clicks" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          <Card className="border-border">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Referral Funnel</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-4">
                {[
                  { label: "Link Clicks", value: totalReferralClicks, pct: 100 },
                  { label: "Referral Orders", value: referredOrders, pct: totalReferralClicks > 0 ? (referredOrders / totalReferralClicks) * 100 : 0 },
                  { label: "Active Conversions", value: orders.filter((o: any) => o.salesperson_id && o.status === "active").length, pct: totalReferralClicks > 0 ? (orders.filter((o: any) => o.salesperson_id && o.status === "active").length / totalReferralClicks) * 100 : 0 },
                ].map((step) => (
                  <div key={step.label} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">{step.label}</span>
                      <span className="font-display text-foreground">{fmt(step.value)} <span className="text-xs text-muted-foreground">({step.pct.toFixed(1)}%)</span></span>
                    </div>
                    <div className="w-full h-2 rounded-full bg-secondary">
                      <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${Math.min(step.pct, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ── Prescription Fulfillment Pipeline ───────── */}
      <div>
        <SectionTitle icon={Package}>Prescription Fulfillment Pipeline</SectionTitle>
        <div className="mt-3">
          {rxPipelineData.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">No prescriptions yet</p>
          ) : (
            <Card className="border-border">
              <CardContent className="pt-6">
                <div className="flex items-end gap-1 justify-between">
                  {rxPipelineData.map((stage, i) => {
                    const maxVal = Math.max(...rxPipelineData.map(s => s.value));
                    const pct = maxVal > 0 ? (stage.value / maxVal) * 100 : 0;
                    return (
                      <div key={stage.name} className="flex-1 text-center">
                        <p className="font-display text-lg text-foreground">{stage.value}</p>
                        <div className="mx-auto w-full max-w-[60px]">
                          <div className="bg-muted rounded-t h-24 relative">
                            <div
                              className="absolute bottom-0 left-0 right-0 bg-primary rounded-t transition-all"
                              style={{ height: `${Math.max(pct, 5)}%`, opacity: 0.6 + (i * 0.05) }}
                            />
                          </div>
                        </div>
                        <p className="text-[10px] text-muted-foreground mt-1 capitalize">{stage.name}</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ── Provider Payouts ─────────────────────────── */}
      <div>
        <SectionTitle icon={DollarSign}>Provider Payouts</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <KPICard label="Total Paid" value={fmtUsd(totalProviderPayoutsPaid)} icon={CreditCard} onClick={() => nav("payouts")} />
          <KPICard label="Pending" value={fmtUsd(pendingProviderPayouts)} icon={Clock} color="text-champagne" onClick={() => nav("payouts")} />
          <KPICard label="Consultations Paid" value={fmt(totalConsultationsPaid)} icon={Stethoscope} onClick={() => nav("payouts")} />
          <KPICard label="Payout Records" value={fmt(providerPayouts.length)} icon={FileText} onClick={() => nav("payouts")} />
        </div>
      </div>

      {/* ── Messaging ────────────────────────────────── */}
      <div>
        <SectionTitle icon={MessageSquare}>Messaging</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <KPICard label="Total Messages" value={fmt(totalMessages)} icon={MessageSquare} onClick={() => nav("messages")} />
          <KPICard label="Unread" value={fmt(unreadMessages)} icon={Mail} color={unreadMessages > 0 ? "text-champagne" : "text-muted-foreground"} onClick={() => nav("messages")} />
          <KPICard label="Conversations" value={fmt(uniqueConversations)} icon={Users} onClick={() => nav("messages")} />
          <KPICard label="Active Senders" value={fmt(uniqueMessagers)} icon={UserCheck} onClick={() => nav("messages")} />
        </div>
      </div>

      {/* ── Patient Engagement ───────────────────────── */}
      <div>
        <SectionTitle icon={Activity}>Patient Engagement</SectionTitle>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <KPICard label="Progress Entries" value={fmt(totalProgressEntries)} icon={TrendingUp} />
          <KPICard label="Active Trackers" value={fmt(uniqueTrackers)} icon={UserCheck} sub="patients logging data" />
          <KPICard label="Marketing Opt-in" value={totalWithPrefs > 0 ? `${((marketingOptIn / totalWithPrefs) * 100).toFixed(0)}%` : "—"} icon={Mail} sub={`${marketingOptIn} of ${totalWithPrefs}`} />
          <KPICard label="Top Metric" value={topMetrics.length > 0 ? topMetrics[0][0] : "—"} icon={ClipboardList} sub={topMetrics.length > 0 ? `${topMetrics[0][1]} entries` : ""} />
        </div>
        {topMetrics.length > 1 && (
          <Card className="border-border mt-4">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Tracked Metrics</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2.5 pt-1">
                {topMetrics.map(([name, value]) => (
                  <div key={name} className="flex items-center gap-3">
                    <span className="text-xs font-medium truncate w-24 capitalize">{name}</span>
                    <div className="flex-1 bg-muted rounded-full h-2">
                      <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${(value / topMetrics[0][1]) * 100}%` }} />
                    </div>
                    <span className="text-xs text-muted-foreground w-8 text-right">{value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {totalWithPrefs > 0 && (
          <Card className="border-border mt-4">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-medium">Notification Preferences Breakdown</CardTitle></CardHeader>
            <CardContent>
              {(() => {
                const prefData = [
                  { name: "Appointments", enabled: notifPrefs.filter((n: any) => n.appointment_reminders).length },
                  { name: "Assessments", enabled: notifPrefs.filter((n: any) => n.assessment_updates).length },
                  { name: "Prescriptions", enabled: notifPrefs.filter((n: any) => n.prescription_updates).length },
                  { name: "Messages", enabled: notifPrefs.filter((n: any) => n.message_notifications).length },
                  { name: "Marketing", enabled: notifPrefs.filter((n: any) => n.marketing_emails).length },
                ].map((d) => ({ ...d, disabled: totalWithPrefs - d.enabled }));
                return (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-1">
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={prefData} layout="vertical" margin={{ left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                        <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
                        <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={90} />
                        <Tooltip
                          contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}
                          formatter={(value: number, name: string) => [value, name === "enabled" ? "Opted In" : "Opted Out"]}
                        />
                        <Bar dataKey="enabled" stackId="a" fill="hsl(var(--primary))" radius={[0, 0, 0, 0]} name="enabled" />
                        <Bar dataKey="disabled" stackId="a" fill="hsl(var(--muted))" radius={[0, 4, 4, 0]} name="disabled" />
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                      {prefData.map((d) => (
                        <div key={d.name} className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">{d.name}</span>
                          <span className="font-medium text-foreground">{((d.enabled / totalWithPrefs) * 100).toFixed(0)}% <span className="text-xs text-muted-foreground">({d.enabled}/{totalWithPrefs})</span></span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}
      </div>

      {/* ── Email Engagement ───────────────────────── */}
      <div>
        <SectionTitle icon={Mail}>Email Engagement</SectionTitle>
        <div className="mt-3">
          <EmailTrackingCard />
        </div>
      </div>
    </div>
  );
};

export default AdminKPIOverview;
